import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Button,
  Table,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../axiosInstance";
import { URL } from "../../api.jsx";
import getPrivilegeList from "../PrivilegeList";

const OtherLspAssignedCases = () => {
  const callerId = localStorage.getItem("userId");
  const admin = localStorage.getItem("isAdmin");
  const privilegeList = getPrivilegeList();

  /* ================= STATES ================= */
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);

  // FILTER
  const [filterType, setFilterType] = useState("");
  const [filterValue, setFilterValue] = useState("");

  // PAGINATION
  const [page, setPage] = useState(0);
  const [size] = useState(10);

  // UPLOAD
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // PAYMENT LINK MODAL
  const [linkModal, setLinkModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [linkAmount, setLinkAmount] = useState("");
  const [linkName, setLinkName] = useState("");
  const [linkContact, setLinkContact] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");

  // FETCH STATUS MODAL
  const [statusModal, setStatusModal] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [fetchingStatus, setFetchingStatus] = useState(false);

  /* ================= FETCH CASES ================= */
  const fetchCases = async (pageNo = 0) => {
    try {
      setLoading(true);
      const params = { callerId, admin, page: pageNo, size };

      if (filterType === "loan_id") params.loanId = filterValue;
      if (filterType === "customer_id") params.customerId = filterValue;
      if (filterType === "phone") params.phoneNumber = filterValue;

      const res = await axiosInstance.get(URL.OTHER_LSP_MY_CASES, { params });

      let list = [];
      if (Array.isArray(res.data)) list = res.data;
      else if (Array.isArray(res.data?.content)) list = res.data.content;

      setCases(list);
      setPage(pageNo);
    } catch (e) {
      if (e.response?.data?.message === "No cases found") {
        setCases([]);
      } else {
        toast.error("Failed to load cases");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ================= INIT ================= */
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    fetchCases(0);
  }, []);

  /* ================= FILTER ================= */
  const handleSearch = () => fetchCases(0);
  const handleReset = () => {
    setFilterType("");
    setFilterValue("");
    fetchCases(0);
  };

  /* ================= UPLOAD EXCEL ================= */
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const res = await axiosInstance.post(URL.OTHER_LSP_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      toast.success(
        `Uploaded: ${d.successCount || 0} success, ${d.duplicateCount || 0} duplicates, ${d.failedCount || 0} failed`
      );
      fetchCases(0);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  /* ================= PAYMENT LINK ================= */
  const openLinkModal = (row) => {
    setSelectedRow(row);
    setLinkAmount(row.overdueoutstanding || "");
    setLinkName(row.customername || "");
    setLinkContact(row.phonenumber || "");
    setGeneratedLink("");
    setLinkModal(true);
  };

  const handleCreateLink = async () => {
    if (!linkAmount || !linkName || !linkContact) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      setGeneratingLink(true);
      const res = await axiosInstance.post(URL.OTHER_LSP_CREATE_LINK, {
        loanId: selectedRow.loanid,
        amount: Number(linkAmount) * 100,
        name: linkName,
        contact: linkContact.startsWith("+91") ? linkContact : `+91${linkContact}`,
      });
      setGeneratedLink(res.data?.short_url || "");
      toast.success("Payment link created");
    } catch {
      toast.error("Failed to create payment link");
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedLink);
    toast.success("Link copied to clipboard");
  };

  /* ================= FETCH ALL PAYMENT STATUS ================= */
  const handleFetchStatus = async (row) => {
    try {
      setFetchingStatus(true);
      setStatusData(null);
      setStatusModal(true);
      const res = await axiosInstance.get(`${URL.OTHER_LSP_FETCH_ALL_LINKS}/${row.loanid}`);
      setStatusData(res.data);
      fetchCases(page);
    } catch {
      toast.error("Failed to fetch payment status");
      setStatusModal(false);
    } finally {
      setFetchingStatus(false);
    }
  };

  /* ================= UI ================= */
  return (
    <>
      {/* ================= FILTER ================= */}
      <Card className="mb-3 p-3">
        <div className="d-flex gap-2 align-items-end flex-wrap justify-content-between">
          <div className="d-flex gap-2 align-items-end flex-wrap">
            <div>
              <Label>Filter Type</Label>
              <select
                className="form-select form-select-sm"
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setFilterValue("");
                }}
              >
                <option value="">Select</option>
                <option value="loan_id">Loan ID</option>
                <option value="customer_id">Customer ID</option>
                <option value="phone">Phone Number</option>
              </select>
            </div>

            {filterType && (
              <div>
                <Label>
                  {filterType === "loan_id"
                    ? "Loan ID"
                    : filterType === "customer_id"
                    ? "Customer ID"
                    : "Phone Number"}
                </Label>
                <Input
                  bsSize="sm"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
              </div>
            )}

            <Button size="sm" color="info" onClick={handleSearch}>
              Search
            </Button>
            <Button size="sm" color="secondary" onClick={handleReset}>
              Reset
            </Button>
          </div>

          {/* UPLOAD BUTTON — privilege based */}
          {privilegeList.otherLspUpload && (
            <div>
              <input
                type="file"
                accept=".xlsx,.xls"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleUpload}
              />
              <Button
                size="sm"
                color="success"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? "Uploading..." : "Upload Cases"}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* ================= TABLE ================= */}
      <Card>
        <CardBody>
          {loading ? (
            <Spinner />
          ) : (
            <Table bordered hover size="sm" responsive className="text-nowrap">
              <thead>
                <tr>
                  <th>Loan ID</th>
                  <th>Customer ID</th>
                  <th>Customer Name</th>
                  <th>Phone</th>
                  <th>Alt Phone</th>
                  <th>Overdue Outstanding</th>
                  <th>Late Fee</th>
                  <th>Aging</th>
                  <th>Disburse Amt</th>
                  <th>Received Amt</th>
                  <th>Recovered Amt</th>
                  <th>Status</th>
                  <th>Assign Date</th>
                  <th>Ref Name</th>
                  <th>Ref Mobile</th>
                  <th>Pay</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {cases.length === 0 ? (
                  <tr>
                    <td colSpan="17" className="text-center">
                      No records found
                    </td>
                  </tr>
                ) : (
                  cases.map((c, i) => (
                    <tr key={i}>
                      <td>{c.loanid}</td>
                      <td>{c.customerid}</td>
                      <td>{c.customername}</td>
                      <td>{c.phonenumber}</td>
                      <td>{c.alternatephonenumber || "-"}</td>
                      <td>{c.overdueoutstanding}</td>
                      <td>{c.latefee || "-"}</td>
                      <td>{c.aging}</td>
                      <td>{c.disburseamount || "-"}</td>
                      <td>{c.receivedamount || "-"}</td>
                      <td>{c.recoveredamount || "-"}</td>
                      <td>
                        <span className="badge bg-warning text-dark">
                          {c.status || "PENDING"}
                        </span>
                      </td>
                      <td>{c.assigndate || "-"}</td>
                      <td>{c.referencename || "-"}</td>
                      <td>{c.referencemobilenumber || "-"}</td>

                      {/* PAY COLUMN */}
                      <td>
                        <Button
                          size="sm"
                          color="success"
                          onClick={() => openLinkModal(c)}
                        >
                          Create Link
                        </Button>
                      </td>

                      {/* ACTION COLUMN */}
                      <td>
                        <Button
                          size="sm"
                          color="info"
                          outline
                          onClick={() => handleFetchStatus(c)}
                        >
                          Check Status
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}

          {/* ================= PAGINATION ================= */}
          <div className="d-flex justify-content-end gap-2 mt-2">
            <Button
              size="sm"
              disabled={page === 0}
              onClick={() => fetchCases(page - 1)}
            >
              Prev
            </Button>
            <span className="align-self-center">Page {page + 1}</span>
            <Button
              size="sm"
              disabled={cases.length < size}
              onClick={() => fetchCases(page + 1)}
            >
              Next
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* ================= PAYMENT LINK MODAL ================= */}
      <Modal isOpen={linkModal} toggle={() => setLinkModal(false)}>
        <ModalHeader toggle={() => setLinkModal(false)}>
          Create Payment Link
        </ModalHeader>
        <ModalBody>
          {selectedRow && (
            <>
              <p><b>Loan ID:</b> {selectedRow.loanid}</p>

              <div className="mb-2">
                <Label>Customer Name</Label>
                <Input
                  bsSize="sm"
                  value={linkName}
                  onChange={(e) => setLinkName(e.target.value)}
                />
              </div>

              <div className="mb-2">
                <Label>Phone Number</Label>
                <Input
                  bsSize="sm"
                  value={linkContact}
                  onChange={(e) => setLinkContact(e.target.value)}
                />
              </div>

              <div className="mb-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  bsSize="sm"
                  value={linkAmount}
                  onChange={(e) => setLinkAmount(e.target.value)}
                />
              </div>

              <div className="d-flex justify-content-end mt-3">
                <Button
                  size="sm"
                  color="primary"
                  disabled={generatingLink}
                  onClick={handleCreateLink}
                >
                  {generatingLink ? "Generating..." : "Generate Link"}
                </Button>
              </div>

              {generatedLink && (
                <div className="mt-3 p-2 border rounded bg-light d-flex align-items-center">
                  <small className="text-break">{generatedLink}</small>
                  <Button
                    size="sm"
                    color="secondary"
                    className="ms-3"
                    onClick={handleCopy}
                  >
                    Copy
                  </Button>
                </div>
              )}
            </>
          )}
        </ModalBody>
      </Modal>

      {/* ================= PAYMENT STATUS MODAL ================= */}
      <Modal isOpen={statusModal} toggle={() => setStatusModal(false)} size="lg">
        <ModalHeader toggle={() => setStatusModal(false)}>
          Payment Status — Loan {statusData?.loanId}
        </ModalHeader>
        <ModalBody>
          {fetchingStatus ? (
            <div className="text-center"><Spinner /></div>
          ) : statusData ? (
            <>
              {statusData.message && !statusData.details && (
                <p className="text-muted">{statusData.message}</p>
              )}

              {statusData.details && (
                <>
                  <div className="d-flex gap-3 mb-3 flex-wrap">
                    <span><b>Total Links:</b> {statusData.totalLinks}</span>
                    <span className="text-success"><b>Captured:</b> {statusData.newCaptured}</span>
                    <span className="text-secondary"><b>Already Processed:</b> {statusData.alreadyProcessed}</span>
                    <span className="text-warning"><b>Pending:</b> {statusData.pending}</span>
                    <span className="text-danger"><b>Failed:</b> {statusData.failed}</span>
                  </div>

                  <div className="mb-3">
                    <span className="me-3"><b>Outstanding:</b> {statusData.currentOverdueOutstanding}</span>
                    <span className="me-3"><b>Recovered:</b> {statusData.currentRecoveredAmount}</span>
                    <span><b>Received:</b> {statusData.currentReceivedAmount}</span>
                  </div>

                  <Table bordered size="sm" responsive className="text-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th>Link ID</th>
                        <th>Status</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusData.details.map((d, i) => (
                        <tr key={i}>
                          <td>{d.plinkId}</td>
                          <td>
                            <span
                              className={`badge ${
                                d.paymentStatus === "SUCCESS"
                                  ? "bg-success"
                                  : d.paymentStatus === "FAILED"
                                  ? "bg-danger"
                                  : "bg-warning text-dark"
                              }`}
                            >
                              {d.paymentStatus}
                            </span>
                          </td>
                          <td>{d.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          ) : null}
        </ModalBody>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />
    </>
  );
};

export default OtherLspAssignedCases;
